import React, { useEffect, useRef, useState, useCallback } from 'react';
import { uploadFile } from '../../api/media';
import { getApiBaseURL } from '../../api/client';
import errorText from '../../utils/errorText';
import { useI18n } from '../../i18n/I18nContext';
import './CourseCoverPicker.css';

/**
 * CourseCoverPicker — a cover CONSTRUCTOR.
 *
 * The user builds their own cover: pick a background style, pick an icon, and
 * write their own title. A live SVG is composed and stored as a self-contained
 * data-URI (so it renders anywhere, no upload needed). An "Upload" tab lets them
 * use a fully custom image instead (via the media API, picked or dropped). Controlled by
 * `value` / `onChange` (the thumbnail URL). A designed cover is read back from `value`, so a course's
 * saved design opens as it was. `courseTitle`: while the cover's title is the course's own, it
 * follows the course title as it is typed.
 */

// Brand-first palette (landing yellow, ink, then the platform accents).
// `fg` is the type colour on that gradient — ink on the light covers.
export const COVER_PALETTES = [
  { id: 'brand',    c1: '#FFD23D', c2: '#F5B000', fg: '#1C1C1E', mark: '#1C1C1E' },
  { id: 'ink',      c1: '#2C2C2E', c2: '#111110', fg: '#FFFFFF', mark: '#FFC107' },
  { id: 'purple',   c1: '#5856D6', c2: '#7D7BEE', fg: '#FFFFFF', mark: '#FFC107' },
  { id: 'coral',    c1: '#FF6B35', c2: '#FF9500', fg: '#FFFFFF', mark: '#FFEBA8' },
  { id: 'teal',     c1: '#30B0C7', c2: '#3B82F6', fg: '#FFFFFF', mark: '#FFC107' },
  { id: 'emerald',  c1: '#34C759', c2: '#1F8A3B', fg: '#FFFFFF', mark: '#FFC107' },
  { id: 'rose',     c1: '#FF3B30', c2: '#FF6B35', fg: '#FFFFFF', mark: '#FFEBA8' },
  { id: 'midnight', c1: '#1C1C1E', c2: '#5856D6', fg: '#FFFFFF', mark: '#FFC107' },
  // Legacy ids still resolve so covers built with the old picker keep rendering.
  { id: 'indigo',   c1: '#5856D6', c2: '#7D7BEE', fg: '#FFFFFF', mark: '#FFC107', legacy: true },
  { id: 'amber',    c1: '#FFD23D', c2: '#F5B000', fg: '#1C1C1E', mark: '#1C1C1E', legacy: true },
  { id: 'sky',      c1: '#30B0C7', c2: '#3B82F6', fg: '#FFFFFF', mark: '#FFC107', legacy: true },
  { id: 'violet',   c1: '#7D7BEE', c2: '#5856D6', fg: '#FFFFFF', mark: '#FFC107', legacy: true },
  { id: 'sunset',   c1: '#FF9500', c2: '#FF3B30', fg: '#FFFFFF', mark: '#FFEBA8', legacy: true },
];
export const COVER_PALETTE_CHOICES = COVER_PALETTES.filter((p) => !p.legacy);

export const COVER_ICONS = [
  { id: 'none',      labelKey: 'authoring.cover.icons.none',        paths: '' },
  { id: 'code',      labelKey: 'authoring.cover.icons.development', paths: '<path d="M9 8l-4 4 4 4"/><path d="M15 8l4 4-4 4"/>' },
  { id: 'chart',     labelKey: 'authoring.cover.icons.business',    paths: '<path d="M4 21V9"/><path d="M11 21V4"/><path d="M18 21v-8"/>' },
  { id: 'globe',     labelKey: 'authoring.cover.icons.language',    paths: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3c3.5 3 3.5 15 0 18M12 3c-3.5 3-3.5 15 0 18"/>' },
  { id: 'pen',       labelKey: 'authoring.cover.icons.design',      paths: '<path d="M4 20l3.5-1L18 8.5 15.5 6 5 16.5z"/><path d="M14 7l3 3"/>' },
  { id: 'megaphone', labelKey: 'authoring.cover.icons.marketing',   paths: '<path d="M3 11v2l14 6V5z"/><path d="M17 8.5a4 4 0 0 1 0 7"/>' },
  { id: 'layers',    labelKey: 'authoring.cover.icons.data',        paths: '<path d="M12 3l9 5-9 5-9-5z"/><path d="M3 12l9 5 9-5"/><path d="M3 16l9 5 9-5"/>' },
  { id: 'cpu',       labelKey: 'authoring.cover.icons.technology',  paths: '<rect x="7" y="7" width="10" height="10" rx="1"/><path d="M10 2v3M14 2v3M10 19v3M14 19v3M2 10h3M2 14h3M19 10h3M19 14h3"/>' },
  { id: 'camera',    labelKey: 'authoring.cover.icons.creative',    paths: '<rect x="3" y="7" width="18" height="13" rx="2"/><circle cx="12" cy="13.5" r="3.5"/><path d="M8 7l1.8-3h4.4L16 7"/>' },
  { id: 'cap',       labelKey: 'authoring.cover.icons.academic',    paths: '<path d="M22 10L12 5 2 10l10 5 10-5z"/><path d="M6 12v5c0 1 2.7 3 6 3s6-2 6-3v-5"/>' },
];

const MAX_MB = 5;

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function wrapTitle(t, maxLen = 15, maxLines = 2) {
  const words = String(t || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (test.length <= maxLen) cur = test;
    else { if (cur) lines.push(cur); cur = w; if (lines.length >= maxLines) break; }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  return lines.slice(0, maxLines).map((l, i) =>
    (i === maxLines - 1 && l.length > maxLen ? `${l.slice(0, maxLen - 1)}…` : l));
}

// The design rides inside the SVG (`data-cover` and its <title>), so it can be read back exactly.
const unescapeXml = (s) => String(s)
  .replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

/** A designed cover's { paletteId, iconId, title }, or null for an uploaded image or no cover. */
export function parseCoverDataUri(uri) {
  if (!uri || !String(uri).startsWith('data:image/svg+xml')) return null;
  let svg;
  try {
    svg = decodeURIComponent(String(uri).replace(/^data:image\/svg\+xml(;utf8)?,/, ''));
  } catch {
    return null;
  }
  const meta = svg.match(/data-cover="([\w-]+)\|([\w-]+)"/);
  const titleTag = svg.match(/<title>([\s\S]*?)<\/title>/);
  if (meta) {
    return { paletteId: meta[1], iconId: meta[2], title: titleTag ? unescapeXml(titleTag[1]) : '' };
  }
  // A cover made before the design was stored: its gradient, its icon's paths, its text lines.
  const stops = [...svg.matchAll(/stop-color="(#[0-9A-Fa-f]{6})"/g)].map((m) => m[1].toUpperCase());
  const palette = COVER_PALETTES.find((p) => p.c1.toUpperCase() === stops[0] && p.c2.toUpperCase() === stops[1]);
  const icon = COVER_ICONS.find((i) => i.paths && svg.includes(i.paths));
  const texts = [...svg.matchAll(/<text[^>]*>([\s\S]*?)<\/text>/g)].map((m) => unescapeXml(m[1]));
  return {
    paletteId: palette ? palette.id : 'brand',
    iconId: icon ? icon.id : 'none',
    // The last text is the "darsly" lockup.
    title: texts.slice(0, -1).join(' '),
  };
}

export function buildCoverDataUri({ paletteId, iconId, title }) {
  const pal = COVER_PALETTES.find((p) => p.id === paletteId) || COVER_PALETTES[0];
  const icon = COVER_ICONS.find((i) => i.id === iconId) || COVER_ICONS[0];
  const lines = wrapTitle(title);
  const two = lines.length > 1;
  const fg = pal.fg || '#ffffff';
  const mark = pal.mark || '#FFC107';
  const font = "'Plus Jakarta Sans',Inter,Arial,sans-serif";
  const textEls = lines.map((l, i) =>
    `<text x="28" y="${two ? 166 + i * 30 : 186}" font-family="${font}" font-size="30" font-weight="800" letter-spacing="-0.5" fill="${fg}">${esc(l)}</text>`).join('');
  const iconEl = icon.paths
    ? `<g fill="none" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" opacity="0.22" transform="translate(250,44) scale(4.2)">${icon.paths}</g>`
    : '';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="225" viewBox="0 0 400 225" data-cover="${pal.id}|${icon.id}">` +
    `<title>${esc(String(title || '').trim())}</title>` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${pal.c1}"/><stop offset="1" stop-color="${pal.c2}"/></linearGradient></defs>` +
    `<rect width="400" height="225" fill="url(#g)"/>` +
    `<circle cx="330" cy="34" r="96" fill="#ffffff" fill-opacity="0.08"/>` +
    `<circle cx="372" cy="212" r="66" fill="#000000" fill-opacity="0.07"/>` +
    iconEl + textEls +
    // Real Darsly logo lockup — amber sunburst mark + white wordmark (bottom-right)
    `<g transform="translate(314,179)" opacity="0.96">` +
      `<g stroke="${mark}" stroke-width="2.6" stroke-linecap="round" transform="translate(7,-2) scale(0.44)">` +
        `<line x1="0" y1="-15" x2="0" y2="-25"/><line x1="-11" y1="-11" x2="-18" y2="-18"/><line x1="11" y1="-11" x2="18" y2="-18"/><line x1="-19" y1="0" x2="-9" y2="0"/><line x1="19" y1="0" x2="9" y2="0"/>` +
      `</g>` +
      `<text x="0" y="18" font-family="${font}" font-size="16" font-weight="800" letter-spacing="-0.6" fill="${fg}">darsly</text>` +
    `</g>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export const DEFAULT_COVER_DESIGN = { paletteId: 'brand', iconId: 'globe', title: '' };

function CourseCoverPicker({ value, onChange, initialTitle = '', courseTitle }) {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const isUploaded = Boolean(value) && !value.startsWith('data:');
  // The saved design, read once: the picker opens on what the course has, not on the defaults.
  const [saved] = useState(() => parseCoverDataUri(value));

  const [tab, setTab] = useState(isUploaded ? 'upload' : 'design');
  const [title, setTitle] = useState(saved ? saved.title : initialTitle);
  const [paletteId, setPaletteId] = useState(saved?.paletteId || DEFAULT_COVER_DESIGN.paletteId);
  const [iconId, setIconId] = useState(saved?.iconId || DEFAULT_COVER_DESIGN.iconId);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragging, setDragging] = useState(false);
  // The cover title follows the course title while the two are the same; typing here stops that.
  const followsCourse = useRef(courseTitle !== undefined && (saved ? saved.title : initialTitle) === courseTitle);

  const emit = useCallback((next) => {
    onChange(buildCoverDataUri({ paletteId, iconId, title, ...next }));
  }, [onChange, paletteId, iconId, title]);

  useEffect(() => {
    if (!followsCourse.current || courseTitle === undefined || courseTitle === title) return;
    setTitle(courseTitle);
    // An uploaded image stays; only a designed cover takes the new title.
    if (tab === 'design') onChange(buildCoverDataUri({ paletteId, iconId, title: courseTitle }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseTitle]);

  const designSrc = buildCoverDataUri({ paletteId, iconId, title });
  const previewSrc = tab === 'upload' && isUploaded ? value : designSrc;

  const switchTab = (tabName) => {
    setUploadError(null);
    setTab(tabName);
    if (tabName === 'design') emit({});
  };

  const handleUpload = (e) => uploadImage(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!uploading) uploadImage(e.dataTransfer?.files?.[0]);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploadError(null);
    if (!file.type.startsWith('image/')) { setUploadError(t('authoring.cover.errorFileType')); return; }
    if (file.size > MAX_MB * 1024 * 1024) { setUploadError(t('authoring.cover.errorFileSize', { mb: MAX_MB })); return; }
    setUploading(true);
    try {
      const res = await uploadFile(file);
      const base = getApiBaseURL();
      const url = res?.url || (res?.id ? `${base}/media/${res.id}/cover` : null);
      if (!url) {
        // Our own sentence (the server answered, but without an address): say it directly.
        setUploadError(t('authoring.cover.errorNoUrl'));
        return;
      }
      onChange(url);
    } catch (err) {
      setUploadError(err?.code === 403
        ? t('authoring.cover.errorNoPermission')
        : errorText(err, t('authoring.cover.errorUploadFailed')));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="ccp">
      {/* Mode tabs */}
      <div className="ccp-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'design'} className={`ccp-tab ${tab === 'design' ? 'is-active' : ''}`} onClick={() => switchTab('design')}>{t('authoring.cover.tabDesign')}</button>
        <button type="button" role="tab" aria-selected={tab === 'upload'} className={`ccp-tab ${tab === 'upload' ? 'is-active' : ''}`} onClick={() => switchTab('upload')}>{t('authoring.cover.tabUpload')}</button>
      </div>

      {/* Live preview */}
      <div className="ccp-preview">
        {previewSrc ? (
          <img src={previewSrc} alt={t('authoring.cover.previewAlt')} className="ccp-preview-img" />
        ) : (
          <div className="ccp-preview-empty">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
            <span>{t('authoring.cover.noCover')}</span>
          </div>
        )}
      </div>

      {tab === 'design' ? (
        <>
          {/* Title */}
          <div className="ccp-field">
            <label htmlFor="ccp-title">{t('authoring.cover.titleLabel')}</label>
            <input
              id="ccp-title"
              type="text"
              value={title}
              maxLength={40}
              placeholder={t('authoring.cover.titlePlaceholder')}
              onChange={(e) => {
                followsCourse.current = false;
                setTitle(e.target.value);
                emit({ title: e.target.value });
              }}
            />
          </div>

          {/* Background */}
          <div className="ccp-section-label">{t('authoring.cover.background')}</div>
          <div className="ccp-palette-row">
            {COVER_PALETTE_CHOICES.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`ccp-palette ${paletteId === p.id ? 'is-selected' : ''}`}
                style={{ background: `linear-gradient(135deg, ${p.c1}, ${p.c2})` }}
                onClick={() => { setPaletteId(p.id); emit({ paletteId: p.id }); }}
                aria-label={t('authoring.cover.paletteBackgroundAriaLabel', { name: t(`authoring.cover.palette.${p.id}`) })}
                aria-pressed={paletteId === p.id}
              />
            ))}
          </div>

          {/* Icon */}
          <div className="ccp-section-label">{t('authoring.cover.icon')}</div>
          <div className="ccp-icon-row">
            {COVER_ICONS.map((ic) => (
              <button
                key={ic.id}
                type="button"
                className={`ccp-icon-btn ${iconId === ic.id ? 'is-selected' : ''}`}
                onClick={() => { setIconId(ic.id); emit({ iconId: ic.id }); }}
                title={t(ic.labelKey)}
                aria-label={t(ic.labelKey)}
                aria-pressed={iconId === ic.id}
              >
                {ic.id === 'none' ? (
                  <span className="ccp-icon-none">∅</span>
                ) : (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: ic.paths }} />
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        /* Upload tab: pick a file or drop one anywhere on this row */
        <div
          className={`ccp-actions ccp-dropzone${dragging ? ' is-dragging' : ''}`}
          onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <button type="button" className="ccp-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? (
              <><span className="ds-spinner ccp-spinner" /> {t('authoring.cover.uploading')}</>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></svg>
                {t('authoring.cover.chooseImage')}
              </>
            )}
          </button>
          {isUploaded && (
            <button type="button" className="ccp-clear-btn" onClick={() => switchTab('design')} disabled={uploading}>{t('authoring.cover.remove')}</button>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUpload} hidden data-no-style />
          <span className="ccp-drop-hint">{t('authoring.cover.dropHint')}</span>
        </div>
      )}

      <p className="ccp-hint">
        {tab === 'design'
          ? t('authoring.cover.hintDesign')
          : t('authoring.cover.hintUpload', { mb: MAX_MB })}
      </p>
      {uploadError && <p className="ccp-error">{uploadError}</p>}
    </div>
  );
}

export default CourseCoverPicker;
